import { v4 as uuid } from 'uuid';
import {
  DEFAULT_FONT_SIZE,
  DEFAULT_ICON_SIZE,
  DEFAULT_LABEL_COLOR,
  isAreaFeature,
  MAX_DESK_ICON_SCALE,
  MIN_DESK_ICON_SCALE,
  type AreaFeature,
  type Coordinate,
  type DeskFeature,
  type DeskShapeFeature,
  type EditableFloorPlan,
  type EditorFeature,
  type IconLabelFeature,
  type MultiPolygonCoordinates,
  type PolygonFeature,
  type PolygonLayer,
  type StructuralType,
  type TextLabelFeature,
} from '../models/editor-feature';
import {
  centrelineToRing, closeRing, insertVertex, moveEdge, moveVertex, removeVertex,
} from './drawing';
import {
  multiPolygonCentroid,
  normalizeDegrees,
  rotateCoordinateAround,
  rotateMultiPolygon,
  scaleMultiPolygon,
  translateCoordinate,
  translateMultiPolygon,
} from './rotation';
import { editableFeaturesOf, selectionKeyOf, type SelectionKey } from './selection';
import type { DeskSeat } from './desk-groups';

/**
 * The result of adding something: the new document, plus the key of what was added so the
 * caller can select it. A newly placed element that is not selected leaves the admin with
 * an inspector showing nothing about the thing they just created.
 */
export interface CreationResult {
  readonly floorPlan: EditableFloorPlan;
  readonly key: SelectionKey;
}

/**
 * Every edit the editor can make to a floorplan, as pure functions.
 *
 * Each returns a new document rather than mutating one, which is what makes undo a matter
 * of keeping the previous value rather than of describing how to reverse each kind of
 * change.
 */

const isSelected = (feature: EditorFeature, keys: ReadonlySet<SelectionKey>): boolean => keys.has(selectionKeyOf(feature));

/**
 * Carries a line's centreline along with a transform of its outline.
 *
 * A line on the floor is stored as the rectangle its centreline sweeps, and the centreline is
 * what keeps its thickness editable. Moving, turning or resizing the rectangle without moving
 * the line with it means the next thickness change re-sweeps the old line — and the shape
 * jumps back to where it was drawn.
 */
const withCentreline = <T extends EditorFeature>(
  feature: T,
  transform: (point: Coordinate) => Coordinate,
): T => {
  if (feature.kind !== 'polygon' || !feature.centreline) {
    return feature;
  }

  const [from, to] = feature.centreline;
  return { ...feature, centreline: [transform(from), transform(to)] };
};

/**
 * Applies a change to the selected features of each collection, leaving everything else
 * identical.
 *
 * Every collection, including the three polygon layers. Leaving them out is what would make a
 * wall selectable and highlighted but immovable — the worst of the three possible states,
 * because it looks like the drag failed rather than like the feature is locked.
 */
const mapSelected = (
  floorPlan: EditableFloorPlan,
  keys: ReadonlySet<SelectionKey>,
  transform: <T extends EditorFeature>(feature: T) => T,
): EditableFloorPlan => ({
  ...floorPlan,
  border: floorPlan.border.map((area) => (isSelected(area, keys) ? transform(area) : area)),
  rooms: floorPlan.rooms.map((area) => (isSelected(area, keys) ? transform(area) : area)),
  floorBackdrop: floorPlan.floorBackdrop.map((area) => (isSelected(area, keys) ? transform(area) : area)),
  desks: floorPlan.desks.map((desk) => (isSelected(desk, keys) ? transform(desk) : desk)),
  deskShapes: floorPlan.deskShapes.map((shape) => (isSelected(shape, keys) ? transform(shape) : shape)),
  labels: floorPlan.labels.map((label) => (isSelected(label, keys) ? transform(label) : label)),
});

/**
 * Moves the selection.
 *
 * Only coordinates change. A move must never touch `rotation` or any other property —
 * dragging a desk and turning it are independent operations that happen to share a
 * selection, and conflating them is how a drag silently straightens furniture.
 */
export const moveSelection = (
  floorPlan: EditableFloorPlan,
  keys: ReadonlySet<SelectionKey>,
  deltaX: number,
  deltaY: number,
): EditableFloorPlan => mapSelected(floorPlan, keys, (feature) => {
  if (isAreaFeature(feature)) {
    return withCentreline(
      { ...feature, coordinates: translateMultiPolygon(feature.coordinates, deltaX, deltaY) },
      (point) => translateCoordinate(point, deltaX, deltaY),
    );
  }

  return { ...feature, coordinates: translateCoordinate(feature.coordinates, deltaX, deltaY) };
});

/**
 * Turns the selection to an absolute angle.
 *
 * Two genuinely different operations share this name. A desk marker or an annotation is a
 * bare point, so its rotation is a property the renderer reads and its coordinates never
 * move. A desk shape is a real polygon, so its vertices are rotated around its own centroid
 * and the property is only bookkeeping — the angle on screen always comes from the actual
 * coordinates.
 *
 * Both must turn the same way for the same number. A positive rotation means clockwise as
 * the admin sees it: that is what an SVG transform does on a Y-down surface, and what the
 * mobile app's canvas does. Vertex rotation happens in the plan's own Y-up coordinates,
 * where the same positive angle turns the other way — so the angle is negated on the way
 * into the geometry. Without this, a desk shape and a label set to 90° face opposite
 * directions, and one drag turns them apart.
 */
export const setSelectionRotation = (
  floorPlan: EditableFloorPlan,
  keys: ReadonlySet<SelectionKey>,
  degrees: number,
): EditableFloorPlan => mapSelected(floorPlan, keys, (feature) => {
  const rotation = normalizeDegrees(degrees);

  if (isAreaFeature(feature)) {
    const shape = feature as AreaFeature;
    const delta = rotation - shape.rotation;
    const pivot = multiPolygonCentroid(shape.coordinates);

    return withCentreline(
      {
        ...feature,
        rotation,
        coordinates: rotateMultiPolygon(shape.coordinates, -delta, pivot),
      },
      (point) => rotateCoordinateAround(point, pivot, -delta),
    );
  }

  return { ...feature, rotation };
});

/** Turns the selection by a relative angle, which is what a nudge button does. */
export const rotateSelectionBy = (
  floorPlan: EditableFloorPlan,
  keys: ReadonlySet<SelectionKey>,
  degrees: number,
): EditableFloorPlan => mapSelected(floorPlan, keys, (feature) => {
  const rotation = normalizeDegrees(feature.rotation + degrees);

  // Every area, not only desk shapes. Testing for a desk shape alone predates rooms and walls
  // being editable, and left the rotate buttons and the rotate grip changing a room's
  // bookkeeping angle while the room itself stayed exactly where it was.
  if (isAreaFeature(feature)) {
    const shape = feature as AreaFeature;
    const pivot = multiPolygonCentroid(shape.coordinates);

    return withCentreline(
      {
        ...feature,
        rotation,
        // Negated for the same reason as the absolute case above: positive means clockwise on
        // screen, and the geometry lives in a Y-up space where that is the other direction.
        coordinates: rotateMultiPolygon(shape.coordinates, -degrees, pivot),
      },
      (point) => rotateCoordinateAround(point, pivot, -degrees),
    );
  }

  return { ...feature, rotation };
});

/** Kept within the bounds, and to two places so a run of presses does not drift to 1.0000001. */
export const clampDeskIconScale = (scale: number): number => Math.min(
  MAX_DESK_ICON_SCALE,
  Math.max(MIN_DESK_ICON_SCALE, Math.round(scale * 100) / 100),
);

/** Sizes are in the plan's own display units; these bounds only stop a size reaching zero or absurdity. */
const clampSize = (size: number): number => Math.min(400, Math.max(1, Math.round(size * 100) / 100));

/**
 * Resizes the selection.
 *
 * "Size" means something different per element type, so this is one action with three
 * implementations rather than one property. A desk shape is a real polygon, so it grows by
 * scaling its own vertices; an annotation's size is the font or icon size the renderer
 * reads. A desk marker has neither — it is a bare point that every client draws at its own
 * fixed size — so it is deliberately left alone rather than given a property nothing would
 * honour.
 */
export const scaleSelection = (
  floorPlan: EditableFloorPlan,
  keys: ReadonlySet<SelectionKey>,
  factor: number,
): EditableFloorPlan => mapSelected(floorPlan, keys, (feature) => {
  if (isAreaFeature(feature)) {
    const shape = feature as AreaFeature;
    const pivot = multiPolygonCentroid(shape.coordinates);
    const scaled = withCentreline(
      { ...feature, coordinates: scaleMultiPolygon(shape.coordinates, factor, pivot) },
      (point): Coordinate => [pivot[0] + (point[0] - pivot[0]) * factor, pivot[1] + (point[1] - pivot[1]) * factor],
    );

    // The outline grew in both directions, so the line it was swept from is now that much
    // thicker too. Left alone, the next thickness change would quietly undo the resize.
    return scaled.kind === 'polygon' && scaled.thickness !== null
      ? { ...scaled, thickness: scaled.thickness * factor }
      : scaled;
  }

  if (feature.kind === 'textLabel') {
    const label = feature as TextLabelFeature;
    return { ...feature, fontSize: clampSize(label.fontSize * factor) };
  }

  if (feature.kind === 'iconLabel') {
    const label = feature as IconLabelFeature;
    return { ...feature, iconSize: clampSize(label.iconSize * factor) };
  }

  // A desk has no footprint to scale, but its icon has a size: that is what grows.
  if (feature.kind === 'desk') {
    const desk = feature as DeskFeature;
    return { ...feature, iconScale: clampDeskIconScale((desk.iconScale ?? 1) * factor) };
  }

  return feature;
});

export const deleteSelection = (
  floorPlan: EditableFloorPlan,
  keys: ReadonlySet<SelectionKey>,
): EditableFloorPlan => ({
  ...floorPlan,
  border: floorPlan.border.filter((area) => !isSelected(area, keys)),
  rooms: floorPlan.rooms.filter((area) => !isSelected(area, keys)),
  floorBackdrop: floorPlan.floorBackdrop.filter((area) => !isSelected(area, keys)),
  desks: floorPlan.desks.filter((desk) => !isSelected(desk, keys)),
  deskShapes: floorPlan.deskShapes.filter((shape) => !isSelected(shape, keys)),
  labels: floorPlan.labels.filter((label) => !isSelected(label, keys)),
});

/**
 * Applies a property change to the selected features of one kind.
 *
 * Scoped by kind because the property panels are: setting a font size means nothing to a
 * desk, and a mixed selection should not have one panel's fields leak onto another's
 * features.
 */
export const updateSelectedProperties = <T extends EditorFeature>(
  floorPlan: EditableFloorPlan,
  keys: ReadonlySet<SelectionKey>,
  kind: T['kind'],
  changes: Partial<T>,
): EditableFloorPlan => {
  const patch = <F extends EditorFeature>(feature: F): F => (
    feature.kind === kind && isSelected(feature, keys) ? { ...feature, ...changes } as F : feature
  );

  return {
    ...floorPlan,
    border: floorPlan.border.map(patch),
    rooms: floorPlan.rooms.map(patch),
    floorBackdrop: floorPlan.floorBackdrop.map(patch),
    desks: floorPlan.desks.map(patch),
    deskShapes: floorPlan.deskShapes.map(patch),
    labels: floorPlan.labels.map(patch),
  };
};

/**
 * The one desk-furniture outline the editor can draw, normalised around its own centre.
 *
 * Nine vertices, matching every desk shape in the stored data — which is also what the
 * backend's tagger looks for when it decides whether a floor polygon is furniture.
 */
const DESK_SHAPE_TEMPLATE: ReadonlyArray<Coordinate> = [
  [0.0535, -0.0838],
  [-0.0522, -0.0949],
  [-0.0731, 0.104],
  [0.0326, 0.1151],
  [0.0339, 0.1027],
  [-0.0593, 0.0929],
  [-0.0411, -0.0811],
  [0.0522, -0.0713],
  [0.0535, -0.0838],
];

/** A new identifier. Dashed, matching every workplace and room feature already stored. */
const newIdentifier = (): string => uuid();

/**
 * The pair of identifiers a newly created feature starts with.
 *
 * A feature the editor creates has nothing stored behind it yet, so its editor handle and
 * the id written on save are the same value. They only diverge for a loaded feature whose
 * stored id could not be used as a handle — see `uniqueIdentifiers` in `normalize-floorplan`.
 */
const newIdentity = (): { id: string; storedId: string } => {
  const id = newIdentifier();
  return { id, storedId: id };
};

/**
 * The icons newly created desks and furniture are drawn as.
 *
 * Carried into every creation path rather than applied afterwards: an office where every desk
 * is a phone booth is one setting, and stamping forty of them and then re-tagging each is the
 * job the setting exists to remove.
 */
export interface DeskIcons {
  readonly desk: string | null;
  readonly shape: string | null;
}

export const NO_DESK_ICONS: DeskIcons = { desk: null, shape: null };

export const addDesk = (
  floorPlan: EditableFloorPlan,
  at: Coordinate,
  roomId: string | null = null,
  iconName: string | null = null,
): CreationResult => {
  const desk: DeskFeature = {
    kind: 'desk',
    // The featureId is what the server keys the desk's Workplaces row by, so it is also
    // what makes the desk bookable once a room has been chosen for it. Without a room it
    // stays drawn-but-unbookable, and the inspector says so.
    ...newIdentity(),
    featureId: newIdentifier(),
    coordinates: at,
    rotation: 0,
    deskType: 'single',
    podId: null,
    iconName,
    capacity: 1,
    roomId,
    unknownProperties: {},
  };

  return { floorPlan: { ...floorPlan, desks: [...floorPlan.desks, desk] }, key: selectionKeyOf(desk) };
};

export const addDeskShape = (
  floorPlan: EditableFloorPlan,
  at: Coordinate,
  ring?: readonly Coordinate[],
  iconName: string | null = null,
): CreationResult => {
  // Wrapped as a MultiPolygon, matching every shape loaded from the database. A plain
  // Polygon here would read as NaN geometry in code written for the other nesting.
  //
  // The ring is given by the caller when a symbol and a real size in metres were chosen, and
  // falls back to the outline this editor has always drawn otherwise — which is what a plan
  // with no scale gets, because a size in metres means nothing on one.
  const coordinates: MultiPolygonCoordinates = [[
    ring ? [...ring] : DESK_SHAPE_TEMPLATE.map(([x, y]): Coordinate => [at[0] + x, at[1] + y]),
  ]];

  const shape: DeskShapeFeature = {
    kind: 'deskShape',
    ...newIdentity(),
    featureId: newIdentifier(),
    coordinates,
    rotation: 0,
    iconName,
    unknownProperties: {},
  };

  return { floorPlan: { ...floorPlan, deskShapes: [...floorPlan.deskShapes, shape] }, key: selectionKeyOf(shape) };
};

export const addTextLabel = (floorPlan: EditableFloorPlan, at: Coordinate, text: string): CreationResult => {
  const label: TextLabelFeature = {
    kind: 'textLabel',
    ...newIdentity(),
    featureId: newIdentifier(),
    coordinates: at,
    text,
    fontSize: DEFAULT_FONT_SIZE,
    color: DEFAULT_LABEL_COLOR,
    rotation: 0,
    unknownProperties: {},
  };

  return { floorPlan: { ...floorPlan, labels: [...floorPlan.labels, label] }, key: selectionKeyOf(label) };
};

export const addIconLabel = (floorPlan: EditableFloorPlan, at: Coordinate, iconName: string): CreationResult => {
  const label: IconLabelFeature = {
    kind: 'iconLabel',
    ...newIdentity(),
    featureId: newIdentifier(),
    coordinates: at,
    // Stored without the prefix, so downstream never has to guess whether one is present.
    iconName: iconName.replace(/^mdi-/, ''),
    iconSize: DEFAULT_ICON_SIZE,
    color: DEFAULT_LABEL_COLOR,
    rotation: 0,
    unknownProperties: {},
  };

  return { floorPlan: { ...floorPlan, labels: [...floorPlan.labels, label] }, key: selectionKeyOf(label) };
};

// ----------------------------------------------------------------- drawn areas

/** Which collection a drawn area belongs to, so one creation path serves all three layers. */
const AREA_COLLECTIONS = {
  border: 'border',
  room: 'rooms',
  floor: 'floorBackdrop',
} as const;

const withArea = (
  floorPlan: EditableFloorPlan,
  polygon: PolygonFeature,
): EditableFloorPlan => {
  const collection = AREA_COLLECTIONS[polygon.layer];

  return { ...floorPlan, [collection]: [...floorPlan[collection], polygon] };
};

/**
 * Adds a drawn area to one of the three polygon layers.
 *
 * Always written as a `MultiPolygon`, unlike a loaded feature, which keeps whatever nesting
 * it arrived with. There is no stored shape to preserve for something the editor just drew,
 * and MultiPolygon is what the rest of this module's geometry assumes.
 */
export const addPolygon = (
  floorPlan: EditableFloorPlan,
  layer: PolygonLayer,
  coordinates: MultiPolygonCoordinates,
  elementType: StructuralType | null = null,
): CreationResult => {
  const polygon: PolygonFeature = {
    kind: 'polygon',
    layer,
    ...newIdentity(),
    featureId: newIdentifier(),
    geometryType: 'MultiPolygon',
    coordinates,
    rotation: 0,
    // Only the floor layer carries one. Setting it elsewhere would invent a distinction the
    // mobile app's per-layer property whitelist cannot carry, so it would vanish on reload.
    elementType: layer === 'floor' ? elementType : null,
    centreline: null,
    thickness: null,
    unknownProperties: {},
  };

  return { floorPlan: withArea(floorPlan, polygon), key: selectionKeyOf(polygon) };
};

/**
 * Adds a straight line to the floor layer: a partition, a counter edge, a marking.
 *
 * Stored the only way the floor layer can carry anything — as the thin rectangle the line
 * sweeps — with the line itself kept alongside, so its thickness can be changed afterwards
 * instead of the line having to be drawn again.
 *
 * `thicknessMetres` is null on a plan with no usable scale, where the rectangle is still drawn
 * at `thicknessUnits` but there is no real-world figure to offer for editing.
 *
 * Undefined for a line with no length, which sweeps nothing.
 */
export const addLine = (
  floorPlan: EditableFloorPlan,
  from: Coordinate,
  to: Coordinate,
  thicknessUnits: number,
  thicknessMetres: number | null,
): CreationResult | undefined => {
  const coordinates = centrelineToRing(from, to, thicknessUnits);

  if (!coordinates) {
    return undefined;
  }

  const created = addPolygon(floorPlan, 'floor', coordinates);
  const line = created.floorPlan.floorBackdrop[created.floorPlan.floorBackdrop.length - 1];

  const withLine: PolygonFeature = {
    ...line,
    centreline: thicknessMetres !== null ? [from, to] : null,
    thickness: thicknessMetres,
  };

  return {
    floorPlan: {
      ...created.floorPlan,
      floorBackdrop: [...created.floorPlan.floorBackdrop.slice(0, -1), withLine],
    },
    key: created.key,
  };
};

/**
 * Applies a change to one drawn area, wherever it lives.
 *
 * Areas are spread across three collections, and a vertex edit names a feature rather than a
 * layer, so the alternative is every caller knowing which array to look in.
 */
/**
 * Breaks one shape into its separate parts, so each can be selected and edited on its own.
 *
 * Some stored plans hold a whole floor's rooms as a *single* feature with thirteen polygons in
 * it. Clicking any of those rooms selects all thirteen, and the selection box is a rectangle
 * across the entire floor — there is no way to move one room, resize it, or give it a name,
 * because as far as every other part of this editor is concerned it is one shape.
 *
 * Deliberately an action rather than something done on load. Splitting changes what is saved,
 * and a plan that silently arrives with thirteen features where it had one is a change nobody
 * asked for and cannot see. This way it is one undoable step the admin took.
 *
 * The first part keeps the original's identity; the rest get fresh identifiers. On the room
 * layer `featureId` is the `Rooms.Id` that makes an area a bookable room, so copying it onto
 * every part would claim the same room several times over — and the mobile app finds a room by
 * first match, which would leave the other parts unreachable. The remaining parts are plain
 * areas, which is what they can honestly be said to be.
 */
export const splitFeatureParts = (
  floorPlan: EditableFloorPlan,
  featureId: string,
): { floorPlan: EditableFloorPlan; keys: SelectionKey[] } => {
  const keys: SelectionKey[] = [];

  const partsOf = <T extends { coordinates: MultiPolygonCoordinates; id: string; storedId: string }>(
    feature: T,
    freshIdentity: (part: T, index: number) => T,
  ): T[] => feature.coordinates.map((polygon, index) => freshIdentity(
      { ...feature, coordinates: [polygon] },
      index,
    ));

  const splitAreas = (features: readonly PolygonFeature[]): PolygonFeature[] => features.flatMap((feature) => {
    if (feature.id !== featureId || feature.coordinates.length < 2) {
      return [feature];
    }

    const parts = partsOf<PolygonFeature>(feature, (part, index) => (index === 0 ? part : {
      ...part,
      ...newIdentity(),
      featureId: newIdentifier(),
    }));

    parts.forEach((part) => keys.push(selectionKeyOf(part)));
    return parts;
  });

  const splitShapes = (features: readonly DeskShapeFeature[]): DeskShapeFeature[] => features.flatMap((feature) => {
    if (feature.id !== featureId || feature.coordinates.length < 2) {
      return [feature];
    }

    const parts = partsOf<DeskShapeFeature>(feature, (part, index) => (index === 0 ? part : {
      ...part,
      ...newIdentity(),
      featureId: newIdentifier(),
    }));

    parts.forEach((part) => keys.push(selectionKeyOf(part)));
    return parts;
  });

  return {
    floorPlan: {
      ...floorPlan,
      border: splitAreas(floorPlan.border),
      rooms: splitAreas(floorPlan.rooms),
      floorBackdrop: splitAreas(floorPlan.floorBackdrop),
      deskShapes: splitShapes(floorPlan.deskShapes),
    },
    keys,
  };
};

const mapArea = (
  floorPlan: EditableFloorPlan,
  featureId: string,
  transform: (feature: PolygonFeature) => PolygonFeature,
): EditableFloorPlan => ({
  ...floorPlan,
  border: floorPlan.border.map((feature) => (feature.id === featureId ? transform(feature) : feature)),
  rooms: floorPlan.rooms.map((feature) => (feature.id === featureId ? transform(feature) : feature)),
  floorBackdrop: floorPlan.floorBackdrop.map((feature) => (feature.id === featureId ? transform(feature) : feature)),
});

/**
 * Moves a single vertex of a drawn area or desk shape.
 *
 * A vertex edit is the one mutation that cannot go through the selection: it names a position
 * inside one feature's geometry, and two features being selected does not mean the same vertex
 * of each should move.
 */
/**
 * Redraws one shape at a given number of points.
 *
 * The shape itself, not the selection: thinning is decided per outline, because a border
 * traced from CAD and a room somebody drew by hand do not want the same amount of detail.
 *
 * Loses the centreline for the same reason a dragged vertex does — the rectangle is no longer
 * the one that centreline sweeps out.
 */
export const setFeatureDetail = (
  floorPlan: EditableFloorPlan,
  featureId: string,
  coordinates: MultiPolygonCoordinates,
): EditableFloorPlan => {
  const redraw = <T extends { coordinates: MultiPolygonCoordinates }>(feature: T): T => ({
    ...feature,
    coordinates,
  });

  return {
    ...mapArea(floorPlan, featureId, (feature) => ({
      ...redraw(feature),
      centreline: null,
      thickness: null,
    })),
    deskShapes: floorPlan.deskShapes.map((shape) => (shape.id === featureId ? redraw(shape) : shape)),
  };
};

export const moveFeatureVertex = (
  floorPlan: EditableFloorPlan,
  featureId: string,
  polygonIndex: number,
  ringIndex: number,
  vertexIndex: number,
  to: Coordinate,
): EditableFloorPlan => {
  const relocate = <T extends { coordinates: MultiPolygonCoordinates }>(feature: T): T => ({
    ...feature,
    coordinates: moveVertex(feature.coordinates, polygonIndex, ringIndex, vertexIndex, to),
  });

  // A moved vertex invalidates the centreline a wall was generated from: the rectangle is no
  // longer the one that centreline sweeps out, so keeping it would make a later thickness
  // change silently snap the wall back to where it used to be.
  const forgetCentreline = (feature: PolygonFeature): PolygonFeature => (
    feature.centreline ? { ...feature, centreline: null, thickness: null } : feature
  );

  return {
    ...mapArea(floorPlan, featureId, (feature) => forgetCentreline(relocate(feature))),
    deskShapes: floorPlan.deskShapes.map((shape) => (shape.id === featureId ? relocate(shape) : shape)),
  };
};

/**
 * Pushes or pulls one side of a drawn area, keeping it parallel — how a room is extended.
 *
 * Forgets a line's centreline for the same reason a dragged vertex does: the outline is no
 * longer the rectangle that line sweeps.
 */
export const moveFeatureEdge = (
  floorPlan: EditableFloorPlan,
  featureId: string,
  polygonIndex: number,
  ringIndex: number,
  edgeIndex: number,
  deltaX: number,
  deltaY: number,
): EditableFloorPlan => {
  const push = <T extends { coordinates: MultiPolygonCoordinates }>(feature: T): T => ({
    ...feature,
    coordinates: moveEdge(feature.coordinates, polygonIndex, ringIndex, edgeIndex, deltaX, deltaY),
  });

  return {
    ...mapArea(floorPlan, featureId, (feature) => {
      const pushed = push(feature);
      return pushed.centreline ? { ...pushed, centreline: null, thickness: null } : pushed;
    }),
    deskShapes: floorPlan.deskShapes.map((shape) => (shape.id === featureId ? push(shape) : shape)),
  };
};

export const insertFeatureVertex = (
  floorPlan: EditableFloorPlan,
  featureId: string,
  polygonIndex: number,
  ringIndex: number,
  afterVertex: number,
  vertex: Coordinate,
): EditableFloorPlan => {
  const add = <T extends { coordinates: MultiPolygonCoordinates }>(feature: T): T => ({
    ...feature,
    coordinates: insertVertex(feature.coordinates, polygonIndex, ringIndex, afterVertex, vertex),
  });

  return {
    ...mapArea(floorPlan, featureId, add),
    deskShapes: floorPlan.deskShapes.map((shape) => (shape.id === featureId ? add(shape) : shape)),
  };
};

/**
 * Removes a vertex, or returns the document unchanged when that would destroy the shape.
 *
 * Unchanged rather than deleting the feature: an admin who removed one vertex too many meant
 * to simplify a shape, not to delete it, and a shape that silently vanished under the cursor
 * is indistinguishable from a bug.
 */
export const removeFeatureVertex = (
  floorPlan: EditableFloorPlan,
  featureId: string,
  polygonIndex: number,
  ringIndex: number,
  vertexIndex: number,
): EditableFloorPlan => {
  const drop = <T extends { coordinates: MultiPolygonCoordinates }>(feature: T): T => {
    const reduced = removeVertex(feature.coordinates, polygonIndex, ringIndex, vertexIndex);
    return reduced ? { ...feature, coordinates: reduced } : feature;
  };

  return {
    ...mapArea(floorPlan, featureId, drop),
    deskShapes: floorPlan.deskShapes.map((shape) => (shape.id === featureId ? drop(shape) : shape)),
  };
};

/**
 * Rebuilds a structural element at a new thickness, around the centreline it was drawn from.
 *
 * Only possible for one that still has its centreline: a wall imported from CAD, or one whose
 * vertices have been dragged, is just a polygon, and there is no line to sweep again.
 */
export const setStructuralThickness = (
  floorPlan: EditableFloorPlan,
  featureId: string,
  thicknessMetres: number,
  thicknessUnits: number,
): EditableFloorPlan => mapArea(floorPlan, featureId, (feature) => {
  if (!feature.centreline) {
    return feature;
  }

  const coordinates = centrelineToRing(feature.centreline[0], feature.centreline[1], thicknessUnits);

  return coordinates ? { ...feature, coordinates, thickness: thicknessMetres } : feature;
});

/** Records a measured scale on the document, so every metre-denominated figure has one. */
export const setScale = (floorPlan: EditableFloorPlan, unitsPerMetre: number | null): EditableFloorPlan => ({
  ...floorPlan,
  unitsPerMetre: unitsPerMetre && unitsPerMetre > 0 ? unitsPerMetre : null,
});

// ------------------------------------------------------------ arranging many

/**
 * Moves each feature by its own offset.
 *
 * Unlike `moveSelection`, which shifts everything by the same delta, this is how alignment
 * and distribution land: each feature goes a different distance, and the whole thing is one
 * history entry because it was one action.
 */
export const applyOffsets = (
  floorPlan: EditableFloorPlan,
  offsets: ReadonlyMap<EditorFeature, Coordinate>,
): EditableFloorPlan => {
  if (offsets.size === 0) {
    return floorPlan;
  }

  // Keyed by the feature's own identity rather than by object reference: the caller resolved
  // the selection against this same document, but a re-render between the two would hand us
  // equal-but-not-identical objects and silently move nothing.
  const byKey = new Map<SelectionKey, Coordinate>();
  offsets.forEach((offset, feature) => byKey.set(selectionKeyOf(feature), offset));

  const shift = <T extends EditorFeature>(feature: T): T => {
    const offset = byKey.get(selectionKeyOf(feature));

    if (!offset) {
      return feature;
    }

    if (isAreaFeature(feature)) {
      return withCentreline(
        { ...feature, coordinates: translateMultiPolygon(feature.coordinates, offset[0], offset[1]) },
        (point) => translateCoordinate(point, offset[0], offset[1]),
      );
    }

    return { ...feature, coordinates: translateCoordinate(feature.coordinates, offset[0], offset[1]) };
  };

  return {
    ...floorPlan,
    border: floorPlan.border.map(shift),
    rooms: floorPlan.rooms.map(shift),
    floorBackdrop: floorPlan.floorBackdrop.map(shift),
    desks: floorPlan.desks.map(shift),
    deskShapes: floorPlan.deskShapes.map(shift),
    labels: floorPlan.labels.map(shift),
  };
};

/**
 * A copy of one feature, offset, with identifiers of its own.
 *
 * New identifiers are the whole point. A desk's `featureId` is the `Workplaces` row that makes
 * it bookable, and two desks claiming the same row is not a duplicate desk — it is one desk
 * drawn twice, which the save would then reconcile into who-knows-what.
 */
const copyOf = (feature: EditorFeature, offsetX: number, offsetY: number): EditorFeature => {
  const identity = { ...newIdentity(), featureId: newIdentifier() };

  if (isAreaFeature(feature)) {
    // A copied line keeps its centreline, moved along with it, so the copy's thickness stays
    // editable and a change to it re-sweeps the copy's own line rather than the original's.
    return withCentreline(
      {
        ...feature,
        ...identity,
        coordinates: translateMultiPolygon(feature.coordinates, offsetX, offsetY),
      },
      (point) => translateCoordinate(point, offsetX, offsetY),
    );
  }

  if (feature.kind === 'desk') {
    return {
      ...feature,
      ...identity,
      coordinates: translateCoordinate(feature.coordinates, offsetX, offsetY),
      // A copy is its own seat, so it starts outside any pod. Inheriting the podId would make
      // the copy move whenever the original's cluster moved, which is not what copying a desk
      // into a new bank of seats means.
      podId: null,
    };
  }

  return { ...feature, ...identity, coordinates: translateCoordinate(feature.coordinates, offsetX, offsetY) };
};

const withFeatures = (
  floorPlan: EditableFloorPlan,
  additions: readonly EditorFeature[],
): EditableFloorPlan => {
  const append = <T extends EditorFeature>(
    existing: readonly T[],
    kind: T['kind'],
    layer?: PolygonLayer,
  ): T[] => [
      ...existing,
      ...additions.filter(
        (feature): feature is T => feature.kind === kind
        && (layer === undefined || (feature as PolygonFeature).layer === layer),
      ),
    ];

  return {
    ...floorPlan,
    border: append(floorPlan.border, 'polygon', 'border'),
    rooms: append(floorPlan.rooms, 'polygon', 'room'),
    floorBackdrop: append(floorPlan.floorBackdrop, 'polygon', 'floor'),
    desks: append(floorPlan.desks, 'desk'),
    deskShapes: append(floorPlan.deskShapes, 'deskShape'),
    labels: [
      ...floorPlan.labels,
      ...additions.filter(
        (feature): feature is TextLabelFeature | IconLabelFeature =>
          feature.kind === 'textLabel' || feature.kind === 'iconLabel',
      ),
    ],
  };
};

export interface MultiCreationResult {
  readonly floorPlan: EditableFloorPlan;
  /** The copies, so the caller can select what it just made rather than what it copied from. */
  readonly keys: SelectionKey[];
}

/**
 * Copies the selection, once per offset.
 *
 * One function for both "duplicate" and "array": a duplicate is an array of one, and keeping
 * them separate means two places that have to agree about identifiers, pods and centrelines.
 */
export const duplicateSelection = (
  floorPlan: EditableFloorPlan,
  keys: ReadonlySet<SelectionKey>,
  offsets: readonly Coordinate[],
): MultiCreationResult => {
  const selected = editableFeaturesOf(floorPlan).filter((feature) => keys.has(selectionKeyOf(feature)));

  if (!selected.length || !offsets.length) {
    return { floorPlan, keys: [] };
  }

  const copies = offsets.flatMap(
    ([offsetX, offsetY]) => selected.map((feature) => copyOf(feature, offsetX, offsetY)),
  );

  return { floorPlan: withFeatures(floorPlan, copies), keys: copies.map(selectionKeyOf) };
};

/**
 * Moves the selected drawn areas to another polygon layer.
 *
 * Only areas: a desk belongs to the workplaces layer by being a desk, and an annotation to
 * the label layer, so there is nowhere else for either to go.
 */
export const moveSelectionToLayer = (
  floorPlan: EditableFloorPlan,
  keys: ReadonlySet<SelectionKey>,
  target: PolygonLayer,
): EditableFloorPlan => {
  const moving: PolygonFeature[] = [];

  const keep = (areas: readonly PolygonFeature[]): PolygonFeature[] => areas.filter((area) => {
    if (!keys.has(selectionKeyOf(area))) {
      return true;
    }

    // A floor type this editor does not know rides in the unknown properties; it is the
    // floor's too, and goes with the known one.
    const { elementType: unknownType, ...otherProperties } = area.unknownProperties;
    moving.push({
      ...area,
      layer: target,
      // Only the floor layer carries a structural tag, and the mobile app's per-layer property
      // whitelist is why: moved anywhere else it would be dropped on the next reload, so it is
      // cleared here rather than left to vanish silently.
      elementType: target === 'floor' ? area.elementType : null,
      unknownProperties: target === 'floor' || unknownType === undefined ? area.unknownProperties : otherProperties,
    });

    return false;
  });

  const remaining = {
    border: keep(floorPlan.border),
    rooms: keep(floorPlan.rooms),
    floorBackdrop: keep(floorPlan.floorBackdrop),
  };

  return {
    ...floorPlan,
    border: [...remaining.border, ...moving.filter((area) => area.layer === 'border')],
    rooms: [...remaining.rooms, ...moving.filter((area) => area.layer === 'room')],
    floorBackdrop: [...remaining.floorBackdrop, ...moving.filter((area) => area.layer === 'floor')],
  };
};

/**
 * Turns desk drawings that are not desks into ordinary floor shapes.
 *
 * For the shapes the server's tagger got wrong — a cabinet, a meeting table, a piece of wall
 * that happened to sit next to a desk. Each becomes a plain floor polygon of the given type, in
 * the same place, with the same identity, so nothing about it moves; it simply stops being
 * offered as furniture to resize and stops being dragged along with a desk.
 *
 * The type is what makes it stick. An untyped floor polygon beside a desk is exactly what the
 * tagger looks for, so it would be a desk again the next time the plan opened.
 */
export const deskShapesToFloor = (
  floorPlan: EditableFloorPlan,
  keys: ReadonlySet<SelectionKey>,
  elementType: StructuralType,
): EditableFloorPlan => {
  const converted: PolygonFeature[] = [];

  const kept = floorPlan.deskShapes.filter((shape) => {
    if (!keys.has(selectionKeyOf(shape))) {
      return true;
    }

    converted.push({
      kind: 'polygon',
      layer: 'floor',
      id: shape.id,
      storedId: shape.storedId,
      featureId: shape.featureId,
      geometryType: 'MultiPolygon',
      coordinates: shape.coordinates,
      rotation: shape.rotation,
      elementType,
      centreline: null,
      thickness: null,
      unknownProperties: shape.unknownProperties,
    });

    return false;
  });

  if (!converted.length) {
    return floorPlan;
  }

  return {
    ...floorPlan,
    deskShapes: kept,
    floorBackdrop: [...floorPlan.floorBackdrop, ...converted],
  };
};

/**
 * Files desks under the rooms they are drawn inside.
 *
 * Applies the backend's relink proposals. Goes through the document like any other edit, so
 * it is one undo step and is written by the save path that moves the seat of capacity with
 * the desk — a second write path for this would be a second place for a room's capacity to
 * drift from the desks in it.
 *
 * Desks not in the map are untouched, and a desk whose room is already what is proposed is
 * left as the same object, so an "apply" that changes nothing does not mark the document
 * dirty.
 */
export const setDeskRooms = (
  floorPlan: EditableFloorPlan,
  roomsByFeatureId: ReadonlyMap<string, string>,
): EditableFloorPlan => ({
  ...floorPlan,
  desks: floorPlan.desks.map((desk) => {
    const roomId = roomsByFeatureId.get(desk.featureId);

    return roomId === undefined || roomId === desk.roomId ? desk : { ...desk, roomId };
  }),
});

/** A desk to create, as the detector describes it. */
export interface DeskPlacement {
  readonly at: Coordinate;
  readonly roomId: string;
}

/**
 * Creates several desks at once.
 *
 * One mutation rather than a loop over `addDesk`, so accepting eighteen detected desks is
 * one undo step. Eighteen would otherwise have to be undone eighteen times, and an admin who
 * wanted to reverse the whole thing would give up halfway and leave nine behind.
 */
export const addDesks = (
  floorPlan: EditableFloorPlan,
  placements: readonly DeskPlacement[],
  iconName: string | null = null,
): { floorPlan: EditableFloorPlan; keys: SelectionKey[] } => {
  let current = floorPlan;
  const keys: SelectionKey[] = [];

  placements.forEach((placement) => {
    const result = addDesk(current, placement.at, placement.roomId, iconName);
    current = result.floorPlan;

    if (result.key) {
      keys.push(result.key);
    }
  });

  return { floorPlan: current, keys };
};

/**
 * Draws several room zones at once, from rings the backend suggested.
 *
 * Each gets a fresh `featureId`, which makes it an area on the room layer and **not** a
 * bookable room: a zone is a room because its identifier is a `Rooms.Id`. Linking an accepted
 * zone to a real room is a separate step the editor does not have yet, and the panel says so
 * rather than leaving the admin to discover it from an empty booking screen.
 */
export const addRoomZones = (
  floorPlan: EditableFloorPlan,
  rings: readonly (readonly Coordinate[])[],
): { floorPlan: EditableFloorPlan; keys: SelectionKey[] } => {
  let current = floorPlan;
  const keys: SelectionKey[] = [];

  rings.forEach((ring) => {
    const result = addPolygon(current, 'room', [[closeRing([...ring])]]);
    current = result.floorPlan;

    if (result.key) {
      keys.push(result.key);
    }
  });

  return { floorPlan: current, keys };
};

/**
 * Stamps a whole arrangement: a desk marker and its furniture outline per seat, in one step.
 *
 * Both halves matter. A desk marker is what makes a seat bookable; the outline is what makes
 * the plan look like an office. Creating them separately is what leaves a floor of markers
 * floating over an empty plate, and it is two undo steps for one gesture.
 *
 * Every desk gets the same `podId`, which records that they were laid out together. That is
 * all it does: the desks are selected, moved, turned and deleted one at a time like any other,
 * and the group is only a unit when it is re-arranged as one from the Group layout panel.
 */
export const addDeskGroup = (
  floorPlan: EditableFloorPlan,
  seats: readonly DeskSeat[],
  roomId: string | null,
  icons: DeskIcons = NO_DESK_ICONS,
): { floorPlan: EditableFloorPlan; keys: SelectionKey[] } => {
  if (!seats.length) {
    return { floorPlan, keys: [] };
  }

  // A pod of one is not a pod: a single desk with a podId would drag every other desk that
  // ever shared it, and there is nothing to keep together.
  const podId = seats.length > 1 ? newIdentifier() : null;

  const desks: DeskFeature[] = seats.map((seat) => ({
    kind: 'desk',
    ...newIdentity(),
    featureId: newIdentifier(),
    coordinates: seat.at,
    rotation: seat.rotationDegrees,
    deskType: podId ? 'pod' : 'single',
    podId,
    iconName: icons.desk,
    capacity: 1,
    roomId,
    unknownProperties: {},
  }));

  const shapes: DeskShapeFeature[] = seats.map((seat) => ({
    kind: 'deskShape',
    ...newIdentity(),
    featureId: newIdentifier(),
    coordinates: [[seat.ring.map(([x, y]): Coordinate => [x, y])]],
    rotation: 0,
    iconName: icons.shape,
    unknownProperties: {},
  }));

  return {
    floorPlan: {
      ...floorPlan,
      desks: [...floorPlan.desks, ...desks],
      deskShapes: [...floorPlan.deskShapes, ...shapes],
    },
    // The markers, not the outlines: selecting what was just stamped should select the
    // things that carry the room and the booking, and pod expansion pulls in the rest.
    keys: desks.map(selectionKeyOf),
  };
};

/**
 * Moves the desks of a group into a new arrangement, in place.
 *
 * Every desk is the one that was already there: same id, same workplace, same room, same icon,
 * same bookings. Only where it sits changes, which is the same thing a drag does — so a bench
 * can be re-spaced or turned into two rows without a single desk being destroyed and recreated,
 * and nothing that hangs off a workplace notices.
 *
 * The outline under each desk moves with it. They are matched to their desks by the caller,
 * since the pairing is geometric and not something the drawing records; an outline nobody
 * matched is left exactly where it is rather than guessed at.
 */
export const relayoutPod = (
  floorPlan: EditableFloorPlan,
  ordered: readonly DeskFeature[],
  seats: readonly DeskSeat[],
  shapesByDeskId: ReadonlyMap<string, DeskShapeFeature>,
): EditableFloorPlan => {
  // Nothing to do rather than something wrong: a seat count that disagrees with the desk count
  // would leave half the group moved and half of it where it was.
  if (!seats.length || seats.length !== ordered.length) {
    return floorPlan;
  }

  const deskMoves = new Map<string, DeskSeat>();
  const shapeMoves = new Map<string, DeskSeat>();

  ordered.forEach((desk, index) => {
    deskMoves.set(desk.id, seats[index]);

    const shape = shapesByDeskId.get(desk.id);

    if (shape) {
      shapeMoves.set(shape.id, seats[index]);
    }
  });

  return {
    ...floorPlan,
    desks: floorPlan.desks.map((desk) => {
      const seat = deskMoves.get(desk.id);

      return seat
        ? { ...desk, coordinates: seat.at, rotation: seat.rotationDegrees }
        : desk;
    }),
    deskShapes: floorPlan.deskShapes.map((shape) => {
      const seat = shapeMoves.get(shape.id);

      return seat
        ? {
          ...shape,
          coordinates: [[seat.ring.map(([x, y]): Coordinate => [x, y])]] as MultiPolygonCoordinates,
          // The ring is drawn already turned, exactly as a freshly stamped group's is, so the
          // rotation the feature records stays at zero rather than turning it a second time.
          rotation: 0,
        }
        : shape;
    }),
  };
};
